import { useState } from "react";
import { Play, Mic } from "lucide-react";

type Clip = {
  id: string;
  title: string;
  type: "video" | "short";
};

const clips: Clip[] = [
  {
    id: "PftoNB-p9K4",
    title: "Full conversation: Wrangling Chaos in agency life",
    type: "video",
  },
  {
    id: "VKtgxMiHgmQ",
    title: "On managing up & protecting your team",
    type: "short",
  },
  {
    id: "gIkjh-VRdH0",
    title: "The real reason projects break",
    type: "short",
  },
];

function ClipCard({ clip, featured = false }: { clip: Clip; featured?: boolean }) {
  const [playing, setPlaying] = useState(false);
  const isShort = clip.type === "short";
  const aspect = isShort ? "aspect-[9/16]" : "aspect-video";
  const thumb = `https://i.ytimg.com/vi/${clip.id}/hqdefault.jpg`;
  const embed = `https://www.youtube-nocookie.com/embed/${clip.id}?autoplay=1&rel=0`;
  const watchUrl = isShort
    ? `https://youtube.com/shorts/${clip.id}`
    : `https://youtu.be/${clip.id}`;

  return (
    <div
      className={`group overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition-all hover:shadow-bold ${
        featured ? "md:col-span-2" : ""
      }`}
    >
      <div className={`relative ${aspect} w-full bg-secondary`}>
        {playing ? (
          <iframe
            src={embed}
            title={clip.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play clip: ${clip.title}`}
            className="group/btn absolute inset-0 h-full w-full"
          >
            <img
              src={thumb}
              alt={clip.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover/btn:scale-105"
            />
            <div className="absolute inset-0 bg-secondary/30 transition-colors group-hover/btn:bg-secondary/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-yellow transition-transform group-hover/btn:scale-110">
                <Play className="h-7 w-7 fill-current" aria-hidden="true" />
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="flex items-start justify-between gap-3 p-4">
        <p className="text-sm font-medium text-foreground line-clamp-2">{clip.title}</p>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-xs font-semibold text-brand-yellow-dark hover:underline"
        >
          YouTube ↗
        </a>
      </div>
    </div>
  );
}

export function PodcastClips() {
  const [featured, ...shorts] = clips;
  return (
    <section className="bg-muted py-20 md:py-28">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            <Mic className="h-4 w-4" aria-label="Podcast" />
            Hear the conversation
          </div>
          <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
            On the podcast circuit
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Quick clips from recent podcast appearances about the book, agency life,
            and leading projects without losing your mind.
          </p>
        </div>

        <div className="mx-auto mt-12 max-w-5xl space-y-6">
          <div className="mx-auto max-w-3xl">
            <ClipCard clip={featured} featured />
          </div>
          <div className="mx-auto grid max-w-3xl gap-6 sm:grid-cols-2">
            {shorts.map((c) => (
              <ClipCard key={c.id} clip={c} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
